import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Avatar } from '../../components/ui/Avatar';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { inputClass } from '../../components/ui/Field';
import { attendanceSummary } from '../../utils/attendance';
import { functionLabel, functionOrder, fullName, memberTypeClass, memberTypeLabel, memberTypeOrder, voiceClass, voiceLabel, voiceOrder } from '../../utils/labels';
import type { ChoirFunction, MemberType, Voice } from '../../types';
export function AdminMembers() {
  const {
    members,
    settings
  } = useApp();
  const [iskanje, setIskanje] = useState('');
  const [tip, setTip] = useState<MemberType | 'vsi'>('vsi');
  const [glas, setGlas] = useState<Voice | 'vsi'>('vsi');
  const [funkcija, setFunkcija] = useState<ChoirFunction | 'vse'>('vse');
  const filtrirani = useMemo(() => members.filter(m => {
    if (iskanje && !fullName(m).toLowerCase().includes(iskanje.toLowerCase())) return false;
    if (tip !== 'vsi' && m.type !== tip) return false;
    if (glas !== 'vsi' && m.voice !== glas) return false;
    if (funkcija !== 'vse' && !m.functions.includes(funkcija)) return false;
    return true;
  }), [members, iskanje, tip, glas, funkcija]);
  return <div className="space-y-5" data-id="element-1183" data-mp="29svfis8i06az">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-1184" data-mp="29tx1hrr1yd7h">
        <div data-id="element-1185" data-mp="251vf3d06763q">
          <h1 className="text-2xl font-semibold text-ink" data-id="element-1186" data-mp="251dmcqcpva0k">Člani</h1>
          <p className="mt-0.5 text-sm text-ink-muted" data-id="element-1187" data-mp="2043pipmzzw2p">
            {members.length} vpisanih oseb · {members.filter(m => m.type === 'aktivni').length} aktivnih
          </p>
        </div>
      </div>

      <Card className="p-4" data-id="element-1188" data-mp="2srppzp0xiklc">
        <div className="grid gap-3 md:grid-cols-4" data-id="element-1189" data-mp="2srhf22od9pcs">
          <div className="relative md:col-span-1" data-id="element-1190" data-mp="3bohz0c0wq38t">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" data-id="element-1191" data-mp="3bp1fdoawsjkq" />
            <input value={iskanje} onChange={e => setIskanje(e.target.value)} placeholder="Išči po imenu…" aria-label="Išči člane" className={`${inputClass} pl-9`} data-id="element-1192" data-mp="3gpp1pwo70c8m" />
          </div>
          <select value={tip} onChange={e => setTip(e.target.value as MemberType | 'vsi')} aria-label="Tip članstva" className={inputClass} data-id="element-1193" data-mp="2sx8ktpg6yqaw">
            <option value="vsi" data-id="element-1194" data-mp="2jyn4m0y6znda">Vsi tipi članstva</option>
            {memberTypeOrder.map((t: MemberType) => <option key={t} value={t} data-id="element-1195" data-mp="2nz72x7ui4x10">
                {memberTypeLabel[t]}
              </option>)}
          </select>
          <select value={glas} onChange={e => setGlas(e.target.value as Voice | 'vsi')} aria-label="Glasovna skupina" className={inputClass} data-id="element-1196" data-mp="m7k6gqruc9x5">
            <option value="vsi" data-id="element-1197" data-mp="22nznxdrhtkkj">Vse glasovne skupine</option>
            {voiceOrder.map(v => <option key={v} value={v} data-id="element-1198" data-mp="2gf4ii1g07mo0">
                {voiceLabel[v]}
              </option>)}
          </select>
          <select value={funkcija} onChange={e => setFunkcija(e.target.value as ChoirFunction | 'vse')} aria-label="Funkcija" className={inputClass} data-id="element-1199" data-mp="2bofuiw3j97tm">
            <option value="vse" data-id="element-1200" data-mp="2ldpj41z648cz">Vse funkcije</option>
            {functionOrder.map(f => <option key={f} value={f} data-id="element-1201" data-mp="2zd7amp3ulzq1">
                {functionLabel[f]}
              </option>)}
          </select>
        </div>
      </Card>

      <Card className="overflow-hidden" data-id="element-1202" data-mp="150984roe47d9">
        <div className="overflow-x-auto viva-scroll" data-id="element-1203" data-mp="151akczh56zwk">
          <table className="w-full min-w-[900px] text-left text-sm" data-id="element-1204" data-mp="1016151ounr2n">
            <thead className="border-b border-line bg-canvas text-[12px] uppercase tracking-wide text-ink-muted" data-id="element-1205" data-mp="102z70ern279l">
              <tr data-id="element-1206" data-mp="12i5piqpzhlv3">
                <th className="px-5 py-3 font-medium" data-id="element-1207" data-mp="16h4rc2gf52ib">Član</th>
                <th className="px-4 py-3 font-medium" data-id="element-1208" data-mp="16fldeii8j3jp">Tip članstva</th>
                <th className="px-4 py-3 font-medium" data-id="element-1209" data-mp="1bhiyzje3yaft">Glas</th>
                <th className="px-4 py-3 font-medium" data-id="element-1210" data-mp="1gfc7ij2vvgj9">Funkcije</th>
                <th className="px-4 py-3 font-medium" data-id="element-1211" data-mp="1gdhvxvdi50ru">Prisotnost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-1212" data-mp="ipr245dkdn1k">
              {filtrirani.map(m => {
              const s = attendanceSummary(m, settings);
              return <tr key={m.id} className="transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1213" data-mp="sof1to1olfgg">
                    <td className="px-5 py-3" data-id="element-1214" data-mp="xi172v5imh28">
                      <Link to={`/admin/clani/${m.id}`} className="flex items-center gap-3" data-id="element-1215" data-mp="1ueqzs8npip">
                        <Avatar member={m} size="sm" data-id="element-1216" data-mp="505ozbmg421g" />
                        <span data-id="element-1217" data-mp="4yku1kw7yh32">
                          <span className="block font-medium text-ink" data-id="element-1218" data-mp="3tmadibos040m">{fullName(m)}</span>
                          <span className="block text-[12px] text-ink-muted" data-id="element-1219" data-mp="3tjxzuaq1wftd">{m.email}</span>
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-3" data-id="element-1220" data-mp="3jm9tenj31jvd">
                      <Badge className={memberTypeClass[m.type]} data-id="element-1221" data-mp="3epjcjxzgek8w">{memberTypeLabel[m.type]}</Badge>
                    </td>
                    <td className="px-4 py-3" data-id="element-1222" data-mp="3aohma6nilij2">
                      <Badge className={voiceClass[m.voice]} data-id="element-1223" data-mp="3ap9a2vavebys">{voiceLabel[m.voice]}</Badge>
                    </td>
                    <td className="px-4 py-3" data-id="element-1224" data-mp="30xmrgdw7dam0">
                      <span className="flex flex-wrap gap-1.5" data-id="element-1225" data-mp="30yeidbeuuk5y">
                        {m.functions.map(f => <Badge key={f} data-id="element-1226" data-mp="2w0d2ezcq70yp">{functionLabel[f]}</Badge>)}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-1227" data-mp="2mxbxh1zljfwm">
                      {m.type === 'aktivni' ? <StatusBadge status={s.status} odstotek={s.odstotek} data-id="element-1228" data-mp="2cc8k7c3rqtd3" /> : <span className="text-[13px] text-ink-muted" data-id="element-1229" data-mp="2hccsieuiwhs8">—</span>}
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        {filtrirani.length === 0 && <p className="px-5 py-10 text-center text-sm text-ink-muted" data-id="element-1230" data-mp="28czow2t2ynff">Ni članov, ki bi ustrezali filtrom.</p>}
      </Card>
    </div>;
}